import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Dice5,
  Gift,
  Pause,
  Play,
  Plus,
  StopCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChannelPicker, CHANNEL_TYPE } from '@/components/ChannelPicker';
import { UserMention } from '@/components/UserMention';
import { MediaPicker } from '@/components/MediaPicker';

/**
 * Giveaways — list + management. Create/End/Pause/Resume/Reroll go through
 * /api/actions/giveaway-* (Redis-bridge fire-and-forget). List refetches every
 * 30s and after every mutation lands.
 */

interface Giveaway {
  messageId: string;
  channelId: string;
  guildId: string;
  hostId: string | null;
  prize: string;
  winnerCount: number;
  startAt: string;
  endAt: string;
  ended: boolean;
  paused: boolean;
  winnerIds: string[];
}

type Filter = 'active' | 'ended' | 'all';

export function Giveaways() {
  const { guildId } = useParams<{ guildId: string }>();
  const [filter, setFilter] = useState<Filter>('active');
  const qc = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['giveaways', guildId, filter],
    queryFn: () =>
      api<{ giveaways: Giveaway[] }>(`/api/guilds/${guildId}/giveaways?status=${filter}`),
    enabled: !!guildId,
    refetchInterval: 30_000,
  });

  const giveaways = data?.giveaways ?? [];
  const refetch = () => {
    // Actions are fire-and-forget through Redis — give the bot a beat to
    // process, then invalidate all filter variants.
    setTimeout(() => qc.invalidateQueries({ queryKey: ['giveaways', guildId] }), 1200);
  };

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="mb-6 text-2xl font-semibold">Giveaways</h1>

      <CreateCard guildId={guildId!} onCreated={refetch} />

      <div className="mb-4 flex items-center justify-between">
        <div className="flex gap-1">
          {(['active', 'ended', 'all'] as const).map((f) => (
            <Button
              key={f}
              size="sm"
              variant={filter === f ? 'default' : 'outline'}
              onClick={() => setFilter(f)}
            >
              {f === 'active' ? 'Aktiv' : f === 'ended' ? 'Beendet' : 'Alle'}
            </Button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground">
          {giveaways.length} {filter === 'active' ? 'aktiv' : filter === 'ended' ? 'beendet' : 'gesamt'}
        </span>
      </div>

      {isLoading && <p className="text-muted-foreground">Lade Giveaways…</p>}
      {error && <p className="text-destructive">Fehler: {(error as Error).message}</p>}

      {!isLoading && giveaways.length === 0 && (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            Keine Giveaways in dieser Ansicht.
          </CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {giveaways.map((g) => (
          <GiveawayRow key={g.messageId} guildId={guildId!} giveaway={g} onAction={refetch} />
        ))}
      </div>
    </div>
  );
}

// --------------------------- Create ---------------------------

const DURATION_UNITS: Record<string, number> = {
  s: 1_000,
  m: 60_000,
  h: 60 * 60_000,
  d: 24 * 60 * 60_000,
};

function CreateCard({ guildId, onCreated }: { guildId: string; onCreated: () => void }) {
  const [channelId, setChannelId] = useState<string | null>(null);
  const [prize, setPrize] = useState('');
  const [winnerCount, setWinnerCount] = useState(1);
  const [durationValue, setDurationValue] = useState(1);
  const [durationUnit, setDurationUnit] = useState<'s' | 'm' | 'h' | 'd'>('d');
  const [imageUrl, setImageUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: (body: object) =>
      api('/api/actions/giveaway-create', {
        method: 'POST',
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      setPrize('');
      setError(null);
      onCreated();
    },
    onError: (err: Error) => setError(err.message),
  });

  const durationMs = durationValue * DURATION_UNITS[durationUnit];
  const canSubmit = channelId && prize.trim() && winnerCount > 0 && durationMs >= 10_000 && !createMut.isPending;

  return (
    <Card className="mb-4">
      <CardHeader>
        <CardTitle className="text-lg">Neues Giveaway</CardTitle>
        <CardDescription>
          Der Bot postet ein Giveaway-Panel in den gewählten Channel. Teilnehmer
          reagieren mit 🎉 zum Beitreten.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div>
          <Label>Ziel-Channel</Label>
          <ChannelPicker
            guildId={guildId}
            value={channelId}
            onChange={setChannelId}
            types={[CHANNEL_TYPE.GUILD_TEXT, CHANNEL_TYPE.GUILD_ANNOUNCEMENT]}
          />
        </div>
        <div>
          <Label>Preis</Label>
          <Input
            value={prize}
            onChange={(e) => setPrize(e.target.value)}
            placeholder="z.B. Discord Nitro"
            maxLength={256}
          />
        </div>
        <div>
          <Label>Großes Bild URL (optional)</Label>
          <div className="flex gap-2">
            <Input
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://…"
            />
            <MediaPicker
              guildId={guildId}
              currentUrl={imageUrl}
              onSelect={(url) => setImageUrl(window.location.origin + url)}
            />
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label>Gewinner</Label>
            <Input
              type="number"
              min={1}
              max={50}
              value={winnerCount}
              onChange={(e) => setWinnerCount(Math.max(1, Math.min(50, Number(e.target.value) || 1)))}
            />
          </div>
          <div>
            <Label>Dauer</Label>
            <Input
              type="number"
              min={1}
              value={durationValue}
              onChange={(e) => setDurationValue(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
          <div>
            <Label>Einheit</Label>
            <select
              value={durationUnit}
              onChange={(e) => setDurationUnit(e.target.value as 's' | 'm' | 'h' | 'd')}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="s">Sekunden</option>
              <option value="m">Minuten</option>
              <option value="h">Stunden</option>
              <option value="d">Tage</option>
            </select>
          </div>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className="flex items-center justify-end gap-2">
          {createMut.isSuccess && <span className="text-sm text-success">Gestartet ✓</span>}
          <Button
            disabled={!canSubmit}
            onClick={() =>
              createMut.mutate({
                guildId,
                channelId,
                prize: prize.trim(),
                winnerCount,
                durationMs,
                imageUrl: imageUrl || undefined,
              })
            }
          >
            <Plus className="mr-1 h-4 w-4" />
            {createMut.isPending ? 'Starte…' : 'Giveaway starten'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// --------------------------- Row + Actions ---------------------------

function GiveawayRow({
  guildId,
  giveaway,
  onAction,
}: {
  guildId: string;
  giveaway: Giveaway;
  onAction: () => void;
}) {
  const g = giveaway;
  const useAct = (path: string) =>
    useMutation({
      mutationFn: () =>
        api(`/api/actions/${path}`, {
          method: 'POST',
          body: JSON.stringify({ guildId, messageId: g.messageId }),
        }),
      onSuccess: onAction,
    });
  const endMut = useAct('giveaway-end');
  const pauseMut = useAct('giveaway-pause');
  const resumeMut = useAct('giveaway-resume');
  const rerollMut = useAct('giveaway-reroll');

  const anyPending =
    endMut.isPending || pauseMut.isPending || resumeMut.isPending || rerollMut.isPending;
  const anyError = endMut.error || pauseMut.error || resumeMut.error || rerollMut.error;

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start gap-3">
          <Gift className="h-5 w-5 shrink-0 text-primary" />
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{g.prize}</span>
              <StatusBadge giveaway={g} />
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {g.winnerCount} Gewinner · Channel <code>#{g.channelId}</code> ·{' '}
              {g.ended ? 'endete' : 'endet'} {new Date(g.endAt).toLocaleString('de-DE')}
            </div>
            {g.hostId && (
              <div className="mt-1 text-xs text-muted-foreground">
                Gehostet von <UserMention id={g.hostId} />
              </div>
            )}
            {g.ended && g.winnerIds.length > 0 && (
              <div className="mt-2 text-xs">
                <strong>Gewinner:</strong>{' '}
                {g.winnerIds.map((id) => (
                  <span key={id} className="mr-2 inline-block">
                    <UserMention id={id} />
                  </span>
                ))}
              </div>
            )}
            {g.ended && g.winnerIds.length === 0 && (
              <div className="mt-2 text-xs text-muted-foreground">
                Keine Gewinner (zu wenige Teilnehmer).
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-1">
              {!g.ended && !g.paused && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={anyPending}
                  onClick={() => pauseMut.mutate()}
                >
                  <Pause className="mr-1 h-3.5 w-3.5" /> Pausieren
                </Button>
              )}
              {!g.ended && g.paused && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={anyPending}
                  onClick={() => resumeMut.mutate()}
                >
                  <Play className="mr-1 h-3.5 w-3.5" /> Fortsetzen
                </Button>
              )}
              {!g.ended && (
                <Button
                  size="sm"
                  variant="destructive"
                  disabled={anyPending}
                  onClick={() => {
                    if (confirm('Giveaway jetzt beenden und auslosen?')) {
                      endMut.mutate();
                    }
                  }}
                >
                  <StopCircle className="mr-1 h-3.5 w-3.5" /> Beenden
                </Button>
              )}
              {g.ended && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={anyPending}
                  onClick={() => rerollMut.mutate()}
                >
                  <Dice5 className="mr-1 h-3.5 w-3.5" /> Reroll
                </Button>
              )}
            </div>
            {anyError && (
              <p className="mt-2 text-xs text-destructive">
                {(anyError as Error).message}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ giveaway }: { giveaway: Giveaway }) {
  const { ended, paused } = giveaway;
  if (ended) {
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Beendet
      </span>
    );
  }
  if (paused) {
    return (
      <span className="rounded-full bg-yellow-500/20 px-2 py-0.5 text-xs text-yellow-500">
        Pausiert
      </span>
    );
  }
  return (
    <span className="rounded-full bg-success/20 px-2 py-0.5 text-xs text-success">Aktiv</span>
  );
}
